import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2, Search, Utensils, Copy, Link2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CUISINE_TYPES = [
  "Asian", "Western", "Italian", "Japanese", "Korean", "Chinese",
  "Thai", "Indian", "Mexican", "Fast Food", "Cafe", "Desserts", "Other"
];

const MAX_ADVANCE_DAYS = 7;

const DEFAULT_SLOTS = Array.from({ length: 24 }, (_, index) => {
  const hour = 10 + Math.floor(index / 2);
  const minute = index % 2 === 0 ? "00" : "30";
  if (hour > 21 || (hour === 21 && minute === "30")) return null;
  return `${String(hour).padStart(2, "0")}:${minute}`;
}).filter(Boolean);

const emptyForm = () => ({
  name: "",
  cuisine_type: "",
  floor: "",
  unit_number: "",
  phone: "",
  email: "",
  description: "",
  opening_hours: "10:00 AM - 10:00 PM",
  logo_url: "",
  capacity: 50,
  accepts_reservations: false,
  is_active: true,
  booking_max_party_size: 12,
  booking_advance_days: MAX_ADVANCE_DAYS,
  booking_cutoff_minutes: 60,
  booking_time_slots: DEFAULT_SLOTS.join(", "),
  booking_notify_email: "",
  booking_notify_whatsapp: "",
  booking_notify_via_email: true,
  booking_notify_via_whatsapp: true,
  booking_notification_template: "",
});

export default function Restaurants() {
  const { user, getAuthHeader } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  const canEdit = ["admin", "manager"].includes(user?.role);
  const canDelete = user?.role === "admin";

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/restaurants`, {
        headers: getAuthHeader()
      });
      setRestaurants(response.data);
    } catch (error) {
      toast.error("Failed to fetch restaurants");
    } finally {
      setLoading(false);
    }
  };

  const payloadFromForm = () => ({
    ...formData,
    booking_max_party_size: Math.min(20, Math.max(10, Number(formData.booking_max_party_size) || 12)),
    booking_advance_days: Math.min(MAX_ADVANCE_DAYS, Math.max(1, Number(formData.booking_advance_days) || MAX_ADVANCE_DAYS)),
    booking_cutoff_minutes: Number(formData.booking_cutoff_minutes) || 60,
    booking_time_slots: String(formData.booking_time_slots || "")
      .split(/[\n,]/)
      .map((slot) => slot.trim())
      .filter(Boolean),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = payloadFromForm();
      if (editingRestaurant) {
        await axios.put(`${API_URL}/api/restaurants/${editingRestaurant.id}`, body, {
          headers: getAuthHeader()
        });
        toast.success("Restaurant updated successfully");
      } else {
        await axios.post(`${API_URL}/api/restaurants`, body, {
          headers: getAuthHeader()
        });
        toast.success("Restaurant created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchRestaurants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (restaurantId) => {
    if (!window.confirm("Are you sure you want to delete this restaurant?")) return;
    try {
      await axios.delete(`${API_URL}/api/restaurants/${restaurantId}`, {
        headers: getAuthHeader()
      });
      toast.success("Restaurant deleted successfully");
      fetchRestaurants();
    } catch (error) {
      toast.error("Failed to delete restaurant");
    }
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setEditingRestaurant(null);
  };

  const openEditDialog = (restaurant) => {
    setEditingRestaurant(restaurant);
    setFormData({
      name: restaurant.name || "",
      cuisine_type: restaurant.cuisine_type || "",
      floor: restaurant.floor || "",
      unit_number: restaurant.unit_number || "",
      phone: restaurant.phone || "",
      email: restaurant.email || "",
      description: restaurant.description || "",
      opening_hours: restaurant.opening_hours || "10:00 AM - 10:00 PM",
      logo_url: restaurant.logo_url || "",
      capacity: restaurant.capacity || 50,
      accepts_reservations: Boolean(restaurant.accepts_reservations),
      is_active: restaurant.is_active !== false,
      booking_max_party_size: restaurant.booking_max_party_size || 12,
      booking_advance_days: restaurant.booking_advance_days || MAX_ADVANCE_DAYS,
      booking_cutoff_minutes: restaurant.booking_cutoff_minutes || 60,
      booking_time_slots: (restaurant.booking_time_slots || DEFAULT_SLOTS).join(", "),
      booking_notify_email: restaurant.booking_notify_email || "",
      booking_notify_whatsapp: restaurant.booking_notify_whatsapp || "",
      booking_notify_via_email: restaurant.booking_notify_via_email !== false,
      booking_notify_via_whatsapp: restaurant.booking_notify_via_whatsapp !== false,
      booking_notification_template: restaurant.booking_notification_template || "",
    });
    setDialogOpen(true);
  };

  const copyDeskLink = async (restaurant) => {
    const token = restaurant.booking_desk_token;
    if (!token) {
      toast.error("Desk link not ready yet. Open and save the restaurant once.");
      return;
    }
    const url = `${window.location.origin}/booking-desk/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Booking desk link copied");
    } catch (error) {
      toast.message(url);
    }
  };

  const regenerateDeskLink = async (restaurantId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/restaurants/${restaurantId}/regenerate-desk-token`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Desk link regenerated");
      const url = `${window.location.origin}/booking-desk/${response.data.booking_desk_token}`;
      try {
        await navigator.clipboard.writeText(url);
      } catch (error) {
        toast.message(url);
      }
      fetchRestaurants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to regenerate desk link");
    }
  };

  const filteredRestaurants = restaurants.filter((restaurant) =>
    restaurant.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    restaurant.cuisine_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="restaurants-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Restaurants</h1>
          <p className="text-muted-foreground mt-1">Manage dining options and table booking settings</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-gradient rounded-none" data-testid="add-restaurant-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Restaurant
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingRestaurant ? "Edit Restaurant" : "Add New Restaurant"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="rounded-none" />
                  </div>
                  <div className="space-y-2">
                    <Label>Cuisine Type *</Label>
                    <Select value={formData.cuisine_type} onValueChange={(value) => setFormData({ ...formData, cuisine_type: value })}>
                      <SelectTrigger className="rounded-none"><SelectValue placeholder="Select cuisine" /></SelectTrigger>
                      <SelectContent>
                        {CUISINE_TYPES.map((cuisine) => <SelectItem key={cuisine} value={cuisine}>{cuisine}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Floor *</Label>
                    <Input required value={formData.floor} onChange={(e) => setFormData({ ...formData, floor: e.target.value })} className="rounded-none" />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit Number *</Label>
                    <Input required value={formData.unit_number} onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })} className="rounded-none" />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="rounded-none" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="rounded-none" />
                  </div>
                  <div className="space-y-2">
                    <Label>Opening Hours</Label>
                    <Input value={formData.opening_hours} onChange={(e) => setFormData({ ...formData, opening_hours: e.target.value })} className="rounded-none" />
                  </div>
                  <div className="space-y-2">
                    <Label>Capacity (seats)</Label>
                    <Input type="number" value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })} className="rounded-none" />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Description</Label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="rounded-none" rows={3} />
                  </div>
                  <div className="space-y-2">
                    <Label>Logo URL</Label>
                    <Input value={formData.logo_url} onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })} className="rounded-none" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                    <Label>Active</Label>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-4">
                  <h3 className="font-semibold">Table booking</h3>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.accepts_reservations}
                      onCheckedChange={(checked) => setFormData({ ...formData, accepts_reservations: checked })}
                      data-testid="restaurant-reservations-switch"
                    />
                    <Label>Enable table booking in chatbot</Label>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Max party size (10-20)</Label>
                      <Input type="number" min={10} max={20} value={formData.booking_max_party_size} onChange={(e) => setFormData({ ...formData, booking_max_party_size: e.target.value })} className="rounded-none" />
                    </div>
                    <div className="space-y-2">
                      <Label>Advance days (max {MAX_ADVANCE_DAYS})</Label>
                      <Input type="number" min={1} max={MAX_ADVANCE_DAYS} value={formData.booking_advance_days} onChange={(e) => setFormData({ ...formData, booking_advance_days: e.target.value })} className="rounded-none" />
                    </div>
                    <div className="space-y-2">
                      <Label>Cutoff (minutes)</Label>
                      <Input type="number" min={0} value={formData.booking_cutoff_minutes} onChange={(e) => setFormData({ ...formData, booking_cutoff_minutes: e.target.value })} className="rounded-none" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Time slots (comma or newline separated, HH:MM)</Label>
                    <Textarea value={formData.booking_time_slots} onChange={(e) => setFormData({ ...formData, booking_time_slots: e.target.value })} className="rounded-none font-mono text-xs" rows={3} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Booking notify email</Label>
                      <Input type="email" value={formData.booking_notify_email} onChange={(e) => setFormData({ ...formData, booking_notify_email: e.target.value })} className="rounded-none" placeholder="restaurant-bookings@example.com" />
                    </div>
                    <div className="space-y-2">
                      <Label>Booking notify WhatsApp</Label>
                      <Input value={formData.booking_notify_whatsapp} onChange={(e) => setFormData({ ...formData, booking_notify_whatsapp: e.target.value })} className="rounded-none" placeholder="2547XXXXXXXX" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                      <Switch checked={formData.booking_notify_via_email} onCheckedChange={(checked) => setFormData({ ...formData, booking_notify_via_email: checked })} />
                      <Label>Notify restaurant by email</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={formData.booking_notify_via_whatsapp} onCheckedChange={(checked) => setFormData({ ...formData, booking_notify_via_whatsapp: checked })} />
                      <Label>Notify restaurant by WhatsApp</Label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Restaurant notification template</Label>
                    <Textarea
                      value={formData.booking_notification_template}
                      onChange={(e) => setFormData({ ...formData, booking_notification_template: e.target.value })}
                      className="rounded-none font-mono text-xs"
                      rows={5}
                      placeholder="Use {restaurant_name}, {reference_id}, {customer_name}, {booking_date}, {booking_time}, {guests}, {desk_url}, ..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">Cancel</Button>
                  <Button type="submit" className="btn-gradient rounded-none">{editingRestaurant ? "Update" : "Create"} Restaurant</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-border rounded-none">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search restaurants by name or cuisine..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-none"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border rounded-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48"><div className="animate-pulse text-muted-foreground">Loading restaurants...</div></div>
          ) : filteredRestaurants.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <Utensils className="h-12 w-12 mb-3 opacity-50" />
              <p>No restaurants found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Name</TableHead>
                  <TableHead>Cuisine</TableHead>
                  <TableHead>Booking</TableHead>
                  <TableHead>Notify</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRestaurants.map((restaurant) => (
                  <TableRow key={restaurant.id}>
                    <TableCell className="font-medium">{restaurant.name}</TableCell>
                    <TableCell>{restaurant.cuisine_type}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium ${restaurant.accepts_reservations ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}`}>
                        {restaurant.accepts_reservations ? `On · max ${restaurant.booking_max_party_size || 12}` : "Off"}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {[restaurant.booking_notify_via_email ? "Email" : null, restaurant.booking_notify_via_whatsapp ? "WhatsApp" : null].filter(Boolean).join(" · ") || "—"}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium ${restaurant.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>
                        {restaurant.is_active ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" title="Copy desk link" onClick={() => copyDeskLink(restaurant)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Regenerate desk link" onClick={() => regenerateDeskLink(restaurant.id)}>
                            <Link2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(restaurant)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {canDelete && (
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(restaurant.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
